import { logger } from '@/lib/logger'
// gateway /admin/* 客户端。
// - 自动注入 Authorization: Bearer <admin session>
// - 错误统一翻成 AdminApiError
// - 401 触发全局 unauthorized handler(由 AuthProvider 注册)→ 清 session + 跳登录
//
// 不在这里做 refresh:gateway 当前没有 refresh 端点,token 12h 过期就重登。
import { AdminApiError, parseErrorBody, toAdminApiError } from './errors'
import { loadToken } from '@/auth/storage'
import type { z } from 'zod'

type UnauthorizedHandler = () => void
let onUnauthorized: UnauthorizedHandler = () => {}
const DEFAULT_PRODUCTION_API_BASE_URL = 'https://api.novelaide.com'
const configuredApiBaseUrl = import.meta.env.VITE_ADMIN_API_BASE_URL

function adminApiUrl(path: string): string {
  return resolveAdminApiUrl(path, {
    configuredBaseUrl: configuredApiBaseUrl,
    hostname: globalThis.location?.hostname
  })
}

export function resolveAdminApiUrl(
  path: string,
  options: { configuredBaseUrl?: string; hostname?: string } = {}
): string {
  if (/^https?:\/\//.test(path)) return path
  const fallbackBaseUrl =
    options.hostname === 'admin.novelaide.com' ? DEFAULT_PRODUCTION_API_BASE_URL : ''
  const baseUrl = normalizeApiBaseUrl(options.configuredBaseUrl ?? fallbackBaseUrl)
  return baseUrl ? `${baseUrl}${path}` : path
}

function normalizeApiBaseUrl(value: string | undefined): string {
  return value?.replace(/\/+$/, '') ?? ''
}

// AuthProvider 启动时调一次,把 logout 函数注册进来
export function setUnauthorizedHandler(handler: UnauthorizedHandler): void {
  onUnauthorized = handler
}

interface FetchOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'
  body?: unknown
  signal?: AbortSignal
  // 跳过鉴权头（/admin/login 与一次性 /admin/bootstrap）。
  skipAuth?: boolean
  // 自定义错误码兜底(默认 'request_failed')
  fallbackErrorCode?: string
}

async function adminFetch<T>(
  path: string,
  responseSchema: z.ZodType<T>,
  opts: FetchOptions = {}
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json'
  }

  const requestToken = opts.skipAuth ? null : loadToken()
  if (requestToken) headers.Authorization = `Bearer ${requestToken}`

  let res: Response
  try {
    res = await fetch(adminApiUrl(path), {
      method: opts.method ?? 'GET',
      headers,
      signal: opts.signal,
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined
    })
  } catch (err) {
    logger.error('admin-api', 'request_failed', {
      stage: 'sending',
      method: opts.method ?? 'GET',
      err
    })
    throw toAdminApiError(err)
  }

  const reqId = res.headers.get('X-NovelAide-Request-Id') ?? undefined
  if (!res.ok)
    logger.error('admin-api', 'request_rejected', {
      reqId,
      status: res.status,
      stage: 'response_headers'
    })

  if (res.status === 401 && !opts.skipAuth) {
    // 注意先解析 body 再触发 unauthorized,确保错误信息能拿到
    const parsed = await parseErrorBody(res, 'unauthorized')
    if (loadToken() === requestToken) onUnauthorized()
    throw new AdminApiError({ status: 401, ...parsed })
  }

  if (!res.ok) {
    const parsed = await parseErrorBody(res, opts.fallbackErrorCode ?? 'request_failed')
    throw new AdminApiError({ status: res.status, ...parsed })
  }

  // 204 / 空 body 也必须经过 schema；只有显式 z.undefined() 才能接受。
  let text: string
  try {
    text = await res.text()
  } catch (err) {
    logger.error('admin-api', 'response_interrupted', {
      reqId,
      status: res.status,
      stage: 'response_body',
      err
    })
    throw toAdminApiError(err)
  }
  let parsed: unknown = undefined
  if (text) {
    try {
      parsed = JSON.parse(text) as unknown
    } catch {
      logger.error('admin-api', 'invalid_response', {
        reqId,
        stage: 'response_parse',
        errorCode: 'invalid_json'
      })
      throw new AdminApiError({
        status: res.status,
        errorCode: 'invalid_response',
        message: 'gateway returned non-JSON body'
      })
    }
  }
  const validated = responseSchema.safeParse(parsed)
  if (!validated.success) {
    logger.error('admin-api', 'invalid_response', {
      reqId,
      stage: 'response_schema',
      errorCode: 'schema_invalid'
    })
    throw new AdminApiError({
      status: res.status,
      errorCode: 'invalid_response',
      message: 'gateway returned an invalid response shape'
    })
  }
  return validated.data
}

// 简洁 API:get/post/patch/put/del
export const adminApi = {
  get: <T>(
    path: string,
    responseSchema: z.ZodType<T>,
    opts?: Omit<FetchOptions, 'method' | 'body'>
  ) => adminFetch(path, responseSchema, { ...opts, method: 'GET' }),

  post: <T>(
    path: string,
    body: unknown,
    responseSchema: z.ZodType<T>,
    opts?: Omit<FetchOptions, 'method' | 'body'>
  ) => adminFetch(path, responseSchema, { ...opts, method: 'POST', body }),

  patch: <T>(
    path: string,
    body: unknown,
    responseSchema: z.ZodType<T>,
    opts?: Omit<FetchOptions, 'method' | 'body'>
  ) => adminFetch(path, responseSchema, { ...opts, method: 'PATCH', body }),

  put: <T>(
    path: string,
    body: unknown,
    responseSchema: z.ZodType<T>,
    opts?: Omit<FetchOptions, 'method' | 'body'>
  ) => adminFetch(path, responseSchema, { ...opts, method: 'PUT', body }),

  del: <T>(
    path: string,
    responseSchema: z.ZodType<T>,
    opts?: Omit<FetchOptions, 'method' | 'body'>
  ) => adminFetch(path, responseSchema, { ...opts, method: 'DELETE' })
}
